import { useTranslation } from 'react-i18next';
import { delay } from '@passionware/platform-js';
import {
  type SimpleEvent,
  useSimpleEventSubscription
} from '@passionware/simple-event';
import { FocusScope } from '@radix-ui/react-focus-scope';
import {
  AlertCircle,
  CheckCircle2,
  CornerDownLeft,
  Lock,
  Pencil,
  Trash2,
  X
} from 'lucide-react';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import React, { useEffect, useRef, useState } from 'react';
import {
  type DefaultValues,
  type FieldValues,
  type Resolver,
  useForm,
  useWatch
} from 'react-hook-form';
import { Button } from 'v5/design-system/Button';
import { Form, FormRootElement } from 'v5/design-system/Form';
import { Kbd } from 'v5/design-system/Kbd';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { useExclusiveEditor } from 'v5/patterns/forms/exclusive-edit/useExclusiveEditor';
import { ConfirmPopover } from 'v5/patterns/widgets/ConfirmPopover';
import {
  activateControlAt,
  PointHit,
  readPointHit
} from 'v5/platform/dom/activateControlAt';
import { cn } from 'v5/platform/dom/cn';
import { findFocusableElement } from 'v5/platform/dom/isFocusable';
import { ModifierKey } from 'v5/primitives/ModifierKey';
import { verticalScrollParent } from 'v5/platform/dom/revealVertically';
import { whenAnimationsSettle } from 'v5/platform/dom/whenAnimationsSettle';
import { revealWhenSettled } from 'v5/platform/dom/whenLayoutSettles';
import { useMemoCompare } from 'v5/platform/lang/useMemoCompare';
import { FieldContextProvider } from './FieldContext';
import { EmptyValueMark } from './fields/EmptyField';
import { RequiredIndicator } from './RequiredIndicator';
import type {
  SchemaFormSize,
  SchemaFormVariant,
  UnitFold,
  UnitTarget
} from './schemaForm.types';
import { UnitAddButton } from './UnitAddButton';
import { UnitErrorAlert } from './UnitErrorAlert';
import { UnitFoldTitle } from './UnitFoldTitle';
import { UnitLayout } from './UnitLayout';
import { UnitTitle, UnitTitleIcon } from './UnitTitle';
import {
  FIELD_PATH_ATTRIBUTE,
  fieldElement,
  UNIT_ADD_ATTRIBUTE
} from './unitPath';
import { saveErrorMessage } from './saveErrorMessage';

/** A “+” beside the pencil: opens the unit to add an entry. */
export type UnitAddAction = {
  label: string;
  /** Nothing to edit yet: the “+” stands alone. */
  editHidden: boolean;
  /** Templates to add from (rate cards): the “+” opens a menu, the blank entry first. */
  menu?: {
    blank: { label: string; description: string };
    templates: ReadonlyArray<{
      id: string;
      label: string;
      /** Second line in the picker; `null` for none. */
      description: string | null;
    }>;
    usedTemplates: ReadonlyArray<string>;
    /** After the templates: where they are kept, for one not listed. */
    manage: { label: string; href: string } | null;
  };
  /**
   * Adds the entry once the unit is in edit mode (`null`: a blank one, else
   * the picked template). Without it the unit focuses its
   * `UNIT_ADD_ATTRIBUTE` control, where the next entry is typed.
   */
  onAdd?(templateId: string | null): void;
};

/** The editor is one entry of a list, edited on its own (a rate, a field group row). */
export type UnitEntry = {
  /** Removes the entry once the user confirms; absent for an entry not saved yet. */
  remove?: { label: string; message: string; run(): Promise<void> };
  /** Not saved yet: Save writes it as it is, even unchanged (a rate card as picked). */
  isNew?: boolean;
  onEditingChange?(editing: boolean): void;
  /** At rest its actions stand beside its first line, not over its corner (`UnitLayout`). */
  actionsBesideContent?: boolean;
};

/** What the editor needs to know of the unit it edits. */
export type UnitEditorUnit = {
  id: string;
  title: string;
  /** Read-only: no edit mode; the lock beside the title says `reason`. */
  editability: { type: 'editable' } | { type: 'read-only'; reason: string };
};

export interface UnitEditorProps<F extends FieldValues> {
  unit: UnitEditorUnit;
  /** The form's values at rest (the unit's saved values, in form shape). */
  formValues: F;
  onSave(data: F): Promise<void>;
  /** Validates the whole form on change and before Save. */
  resolver: Resolver<F>;
  /** Rendered inside the unit's form: its fields, or a list of rows. */
  content: React.ReactNode;
  /** Has required fields: the title marks it while editing. */
  required: boolean;
  /** Its fields show labels of their own: the title stands out from them. */
  groupTitle: boolean;
  /**
   * The form's values hold nothing: at rest the title is followed by "no
   * value" and nothing shows under it (nor does it fold). Read from the
   * form, so a save shows its value before the host's values catch up.
   */
  isEmpty?(values: F): boolean;
  variant: SchemaFormVariant;
  size: SchemaFormSize;
  /**
   * `SchemaSectionsProps.editUnitSignal`: a target with this unit's id enters
   * edit mode, as the edit button does, focuses its field and scrolls the
   * unit into view. A read-only unit scrolls into view and lights up.
   */
  editUnitSignal?: SimpleEvent<UnitTarget>;
  add?: UnitAddAction;
  /** One entry of a list: no title at rest, its actions over its corner. */
  entry?: UnitEntry;
  /**
   * The unit folds away from its title; folded, it shows `summary` beside
   * the title (a list's count) instead of its content.
   */
  fold?: UnitFold & { summary?: string };
  /** Beside the title (badges); told whether the unit is in edit mode. */
  titleExtras?(state: { isEditing: boolean }): React.ReactNode;
  /** At rest, before the add and edit buttons: an action of the host's. */
  restActions?: React.ReactNode;
  /** At rest, over the content: how a rest action failed (a `UnitErrorAlert`). */
  restNotice?: React.ReactNode;
  /** See `UnitLayoutProps.compactTitleRow`. */
  compactTitleRow?: boolean;
  /** Before the title (`UnitTitleIcon`): what kind of value the unit holds. */
  icon?: React.ReactNode;
  /** See `UnitLayoutProps.actionsTab`; Save shows its ⌘↵ there. */
  actionsTab?: boolean;
}

/**
 * Display mode as in the designs: a value is semibold in whichever control
 * shows it (input, textarea, select or picker trigger, date segments), while
 * text around it (a rate's unit and fees, notes) keeps its own weight and so
 * does the placeholder of an empty value. Edit mode leaves each editor its
 * own type.
 */
const DISPLAY_VALUES_CLASS_NAME =
  '[&_:is(input,textarea,button,[role=group])]:font-semibold [&_::placeholder]:font-normal';

/**
 * One independently saveable unit: its own react-hook-form instance over
 * `formValues`, display ⇄ edit toggle, save / cancel / remove actions, around
 * whatever `content` renders from that form — a unit's fields (`UnitForm`),
 * a list's rows (`ListUnitView`) or any plain form fields. Units take turns
 * in edit mode through the nearest `ExclusiveEditProvider`: another unit, a
 * new list row or Esc never drop unsaved changes without asking; Cancel does.
 * It knows nothing of schemas: what a schema's unit adds around it
 * (inheritance) is `SchemaUnitEditor`'s, through the slots.
 */
export function UnitEditor<F extends FieldValues>({
  unit,
  formValues,
  onSave,
  resolver,
  content,
  required,
  groupTitle,
  isEmpty,
  variant,
  size,
  editUnitSignal,
  add,
  entry,
  fold: foldWhenFilled,
  titleExtras,
  restActions,
  restNotice,
  compactTitleRow = false,
  icon = null,
  actionsTab = false
}: UnitEditorProps<F>) {
  const { t } = useTranslation('patterns', { keyPrefix: 'SchemaForm.unit' });
  const { t: tForm } = useTranslation('patterns', { keyPrefix: 'SchemaForm' });
  const stableValues = useMemoCompare(formValues);
  const [isEditing, setIsEditing] = useState(false);
  const contentRef = useRef<HTMLFieldSetElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const editButtonRef = useRef<HTMLButtonElement>(null);
  /** ⌘/Ctrl+Enter presses it: one way to save, whatever the button does. */
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  const addButtonRef = useRef<HTMLButtonElement>(null);
  /** Edit mode came from the “+”: focus goes to where the next entry is typed. */
  const addingRef = useRef<{ templateId: string | null } | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  /** Edit mode ended scrolled down the editor: where the unit's top goes. */
  const revealTopRef = useRef<number | null>(null);
  const reducedMotion = useReducedMotion();
  const lastClickPosition = useRef<PointHit | null>(null);
  /** Edit mode ended; set until focus has gone back to the edit button. */
  const returnFocusRef = useRef(false);
  /** Edit mode came from `editUnitSignal`: the unit scrolls into view. */
  const revealRef = useRef(false);
  /** The field `editUnitSignal` named, focused once edit mode is on. */
  const targetPathRef = useRef<string | null>(null);
  const saveFlash = useTimedFlag(2000);
  /** A read-only unit `editUnitSignal` reached. */
  const revealFlash = useTimedFlag(1500);

  const form = useForm<F>({
    defaultValues: stableValues as DefaultValues<F>,
    // At rest the form follows the saved values whenever they change; while
    // editing, the draft is the user's. The host's values may arrive before
    // or after a Save settles (the app's cache notifies on a later task).
    values: isEditing ? undefined : stableValues,
    resolver,
    // A field's problem shows once the field is left (or Save is tried),
    // then follows each change.
    mode: 'onTouched',
    reValidateMode: 'onChange'
  });
  const { isSubmitting, errors } = form.formState;
  const values = useWatch({ control: form.control }) as F;
  const empty = isEmpty?.(values) ?? false;
  const isDirty = form.formState.isDirty || entry?.isNew === true;

  const editable = unit.editability.type === 'editable';
  const emptyAtRest = empty && !isEditing;
  const fold = empty ? undefined : foldWhenFilled;
  const effectiveMode: 'display' | 'edit' =
    editable && isEditing ? 'edit' : 'display';

  const exclusiveEditor = useExclusiveEditor({
    title: unit.title,
    isEditing,
    isDirty,
    isSaving: isSubmitting,
    discard: resetForm,
    elementRef: formRef
  });

  function startEditing(
    clickPosition: PointHit | null,
    reveal = false,
    adding: { templateId: string | null } | null = null
  ) {
    // Double-clicking inside the unit being edited selects text.
    if (!editable || isEditing) return;
    exclusiveEditor.requestEdit(() => {
      // An empty unit opens unfolded too: saved, it shows what was added.
      if (foldWhenFilled?.collapsed) foldWhenFilled.setCollapsed(false);
      form.clearErrors();
      lastClickPosition.current = clickPosition;
      revealRef.current = reveal;
      addingRef.current = adding;
      setIsEditing(true);
    });
  }

  // A new entry (a list's “+”, which already asked the session) opens as it
  // appears: its editor exists only from this render on.
  const isNewEntry = entry?.isNew === true;
  const latestStartEditing = useRef(startEditing);
  latestStartEditing.current = startEditing;
  useEffect(() => {
    if (isNewEntry) latestStartEditing.current(null, true);
  }, [isNewEntry]);

  /** Leaves edit mode showing `values`: the saved ones, or what a Save wrote. */
  function leaveEditMode(values: F) {
    revealTopRef.current = stickyLineAboveTop(
      formRef.current,
      headerRef.current
    );
    lastClickPosition.current = null;
    form.reset(values);
    returnFocusRef.current = true;
    setIsEditing(false);
  }

  function resetForm() {
    leaveEditMode(stableValues);
  }

  useEffect(() => {
    if (!isEditing || !contentRef.current) return;
    const adding = addingRef.current;
    addingRef.current = null;
    if (adding && add?.onAdd) {
      add.onAdd(adding.templateId);
      return;
    }
    if (adding) {
      const target = contentRef.current.querySelector<HTMLElement>(
        `[${UNIT_ADD_ATTRIBUTE}]`
      );
      if (target) {
        target.focus();
        return;
      }
    }
    if (revealRef.current) {
      revealRef.current = false;
      focusTarget(contentRef.current);
      return;
    }
    if (
      lastClickPosition.current &&
      activateControlAt(
        contentRef.current,
        lastClickPosition.current,
        FIELD_PATH_ATTRIBUTE
      )
    ) {
      return;
    }
    if (!contentRef.current.contains(document.activeElement)) {
      findFocusableElement(contentRef.current)?.focus();
    }
  }, [isEditing]);

  const onEditingChange = useRef(entry?.onEditingChange);
  onEditingChange.current = entry?.onEditingChange;
  useEffect(() => {
    onEditingChange.current?.(isEditing);
  }, [isEditing]);

  const scrollBehavior = reducedMotion ? 'auto' : 'smooth';

  /** Focuses the targeted field (else the first) and brings it into view. */
  function focusTarget(content: HTMLElement) {
    const path = targetPathRef.current;
    targetPathRef.current = null;
    const field = path === null ? null : fieldElement(content, path);
    // A locked field takes no focus: the unit's first field does.
    const focusable =
      (field && findFocusableElement(field)) ?? findFocusableElement(content);
    focusable?.focus({ preventScroll: true });
    if (formRef.current) {
      void revealWhenSettled(formRef.current, scrollBehavior, field);
    }
  }

  /** A jump landed here: edit at its field, or, read-only, show the unit. */
  function reachTarget(fieldPath: UnitTarget['fieldPath']) {
    if (!editable) {
      if (fold?.collapsed) fold.setCollapsed(false);
      if (formRef.current) {
        void revealWhenSettled(formRef.current, scrollBehavior).then(
          revealFlash.trigger
        );
      }
      return;
    }
    targetPathRef.current = fieldPath;
    if (isEditing) {
      // Already editing: to the field, the draft as it is.
      const content = contentRef.current;
      if (
        content &&
        (fieldPath !== null || !content.contains(document.activeElement))
      ) {
        focusTarget(content);
      }
      return;
    }
    startEditing(null, true);
  }

  useSimpleEventSubscription(editUnitSignal?.addListener, target => {
    if (target.unitId === unit.id) reachTarget(target.fieldPath);
  });

  useEffect(() => {
    const revealTop = revealTopRef.current;
    revealTopRef.current = null;
    const card = formRef.current;
    if (isEditing || revealTop === null || !card) return;
    // Another unit took over (it opened as this one closed): it is where the
    // user is now, so this one doesn't pull the view back to itself.
    const scroller = verticalScrollParent(card);
    const root = scroller ?? document;
    if (root.querySelector('[data-unit-editing]')) return;
    // The editor shrinks back to the display from the bottom up. Scrolled
    // down a long editor, its top was out of view above the sticky row, so
    // all of it could end up above the view, with the units below in its
    // place: its top comes back to the line the row stuck to, once the card
    // has eased into its display frame (its margin moves its top too).
    let cancelled = false;
    void whenAnimationsSettle(card).then(() => {
      if (cancelled) return;
      const delta = card.getBoundingClientRect().top - revealTop;
      if (delta > -1) return;
      (scroller ?? window).scrollBy({
        top: delta,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    });
    return () => {
      cancelled = true;
    };
  }, [isEditing]);

  useEffect(() => {
    if (isEditing || !returnFocusRef.current) return;
    // Leaving edit mode unmounts the field or button that had focus, so focus
    // falls to the page and Tab would start over inside this unit. The edit
    // button takes it instead: Tab goes on to the next unit. A unit that
    // opened meanwhile has focus already and keeps it.
    if (document.activeElement === document.body) {
      (editButtonRef.current ?? addButtonRef.current)?.focus({
        preventScroll: true
      });
    }
    returnFocusRef.current = false;
  }, [isEditing]);

  // Esc in the form leaves edit mode and goes no further: a dialog around the unit (Radix hears
  // it on the document, before the unit) stays open. An open list in a field closes first; a
  // popover's own Esc is pressed outside the form.
  useEffect(() => {
    if (!isEditing) return;
    const takeEscape = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        event.key !== 'Escape' ||
        !(target instanceof Element) ||
        !formRef.current?.contains(target) ||
        target.getAttribute('aria-expanded') === 'true'
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      exclusiveEditor.requestLeave();
    };
    window.addEventListener('keydown', takeEscape, { capture: true });
    return () =>
      window.removeEventListener('keydown', takeEscape, { capture: true });
  }, [isEditing, exclusiveEditor]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isEditing) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      if (formRef.current?.contains(document.activeElement)) {
        e.preventDefault();
        e.stopPropagation();
        saveButtonRef.current?.click();
      }
    }
  };

  // Save is only greyed out: the native submit and the form's ⌘Enter still
  // arrive at rest, with nothing changed, or while a save is running.
  const submit = async () => {
    if (
      !isEditing ||
      !(form.formState.isDirty || entry?.isNew === true) ||
      form.formState.isSubmitting
    ) {
      return;
    }
    // The fields are disabled while saving, so the one in use loses focus.
    const focused = document.activeElement;
    let failed = false;
    await form.handleSubmit(async data => {
      try {
        await onSave(data);
        saveFlash.trigger();
        leaveEditMode(data);
      } catch (e) {
        failed = true;
        form.setError('root', {
          message: saveErrorMessage(e, tForm('saveInvalidData'))
        });
      }
    })();
    if (!failed) return;
    await delay(200);
    if (focused instanceof HTMLElement) focused.focus({ preventScroll: true });
    formRef.current
      ?.querySelector('[data-element="server-error"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  // Empty at rest, a required unit still says so: it lacks what it needs.
  const showRequired = required && (effectiveMode === 'edit' || emptyAtRest);

  const title = (
    <UnitTitle groupTitle={groupTitle}>
      {/* One line: a long title is cut, not wrapped away from its icon and mark. */}
      <span className="flex max-w-full min-w-0 items-center gap-x-2">
        {icon !== null && <UnitTitleIcon>{icon}</UnitTitleIcon>}
        {fold && !isEditing ? (
          <UnitFoldTitle
            title={unit.title}
            fold={fold}
            summary={fold.summary}
          />
        ) : (
          <span className="min-w-0 truncate">{unit.title}</span>
        )}
        {showRequired && <RequiredIndicator />}
        {emptyAtRest && <EmptyValueMark />}
      </span>
      {titleExtras?.({ isEditing })}
    </UnitTitle>
  );

  const actions = (
    <>
      {isEditing && (
        <SimpleTooltip title="Cancel changes" align="end">
          <Button
            variant="ghost"
            size="sm"
            onClick={resetForm}
            disabled={isSubmitting}
            aria-label={t('cancelEditing', { title: unit.title })}
          >
            <X />
            Cancel
          </Button>
        </SimpleTooltip>
      )}
      {unit.editability.type === 'read-only' ? (
        <SimpleTooltip align="end" title={unit.editability.reason}>
          <Button
            variant="ghost"
            size="sm"
            layout="icon"
            visuallyDisabled
            aria-label={t('readOnly', { title: unit.title })}
          >
            <Lock />
          </Button>
        </SimpleTooltip>
      ) : isEditing ? (
        <SimpleTooltip title="Save" align="end">
          <Button
            ref={saveButtonRef}
            variant="primary"
            size="sm"
            type="submit"
            layout="normal"
            // Not greyed for a problem: pressing Save is what shows it.
            visuallyDisabled={!isDirty}
            disabled={isSubmitting}
          >
            {errors.root ? (
              <AlertCircle />
            ) : isSubmitting ? (
              // Not while a field is checked on leaving it: swapping the icon
              // then, between one field and the next, makes the focus trap
              // take the focus back to the editor.
              <LoadingSpinner />
            ) : (
              <CheckCircle2 />
            )}
            Save
            {actionsTab && (
              <Kbd className="bg-bg-default/20 text-bg-default">
                <ModifierKey />
                <CornerDownLeft className="size-3" />
              </Kbd>
            )}
          </Button>
        </SimpleTooltip>
      ) : (
        <>
          {restActions}
          {add && (
            <UnitAddButton
              ref={addButtonRef}
              add={add}
              onAdd={templateId => startEditing(null, false, { templateId })}
              // Focus handed back on leaving edit mode opens no tooltip.
              onFocus={event => {
                if (returnFocusRef.current) event.preventDefault();
              }}
            />
          )}
          {!add?.editHidden && (
            <SimpleTooltip title="Switch to edit mode" align="end">
              <Button
                ref={editButtonRef}
                variant="ghost"
                size="sm"
                layout="icon"
                onClick={() => startEditing(null)}
                // Focus handed back on leaving edit mode opens no tooltip.
                onFocus={event => {
                  if (returnFocusRef.current) event.preventDefault();
                }}
                aria-label={t('edit', { title: unit.title })}
              >
                <Pencil />
              </Button>
            </SimpleTooltip>
          )}
          {entry?.remove && (
            <ConfirmPopover
              message={entry.remove.message}
              confirmLabel={entry.remove.label}
              onConfirm={entry.remove.run}
            >
              <Button
                variant="ghost"
                size="sm"
                layout="icon"
                aria-label={`${entry.remove.label} ${unit.title}`}
              >
                <Trash2 />
              </Button>
            </ConfirmPopover>
          )}
        </>
      )}
    </>
  );

  const contentBox = (
    // Disabled while saving: what is typed then would be lost by the reset after it.
    <fieldset
      ref={contentRef}
      disabled={isSubmitting}
      className={cn(
        'm-0 flex min-w-0 flex-col gap-2 border-0 p-0',
        // Under a title with an icon, values start at the title's text: the
        // icon slot and the gap (5 + 2) are a gutter rows hang their leads in.
        icon !== null &&
          '[--lead-gutter:calc(var(--spacing)*7)] pl-(--lead-gutter)',
        effectiveMode === 'display' && DISPLAY_VALUES_CLASS_NAME
      )}
    >
      {content}
    </fieldset>
  );
  const showsContent = !emptyAtRest && !(fold?.collapsed && !isEditing);

  const body = (
    <UnitLayout
      titleSlot={title}
      actionsSlot={actions}
      contentSlot={showsContent ? contentBox : null}
      aboveContentSlot={
        <AnimatePresence>{!isEditing && restNotice}</AnimatePresence>
      }
      belowContentSlot={
        <AnimatePresence>
          {isEditing && errors.root?.message && (
            <UnitErrorAlert
              title="Failed to save"
              message={errors.root.message}
              onClose={() => form.clearErrors('root')}
            />
          )}
        </AnimatePresence>
      }
      isEditing={isEditing}
      headerRef={headerRef}
      submitState={saveFlash.value ? 'success' : null}
      revealed={revealFlash.value}
      interactive={editable}
      floatingActionsAtRest={entry !== undefined}
      actionsBesideContent={entry?.actionsBesideContent === true}
      actionsTab={actionsTab}
      stickyTitle={fold !== undefined && !fold.collapsed}
      compactTitleRow={compactTitleRow}
      contentWrapper={content =>
        isEditing ? (
          <FocusScope
            loop
            trapped
            asChild
            // Focus before edit mode sat in this unit's fields, remounted
            // since: going back there only blurs the unit that opened next.
            onUnmountAutoFocus={event => event.preventDefault()}
          >
            {content}
          </FocusScope>
        ) : (
          content
        )
      }
    >
      <FormRootElement
        ref={formRef}
        data-testid={`schema-unit-${unit.id}`}
        onSubmit={() => void submit()}
        onMouseDownCapture={e => {
          // The second press of a double click that opens the editor would
          // select a word, which then grows over the fields as they swap in.
          if (!(e.target instanceof Node && e.currentTarget.contains(e.target)))
            return;
          if (e.detail > 1 && editable && !isEditing) e.preventDefault();
        }}
        onDoubleClickCapture={e => {
          // React bubbles events from portals (a hover card, a menu) through
          // the unit; only a double click on the unit itself opens it.
          if (!(e.target instanceof Node && e.currentTarget.contains(e.target)))
            return;
          startEditing(
            readPointHit(e.clientX, e.clientY, FIELD_PATH_ATTRIBUTE)
          );
        }}
        onKeyDown={handleKeyDown}
      />
    </UnitLayout>
  );

  return (
    <Form {...form}>
      <FieldContextProvider
        value={{
          mode: effectiveMode,
          variant:
            variant === 'plain' ? (isEditing ? 'inline' : 'plain') : variant,
          size,
          basePath: '',
          ghost: false
        }}
      >
        {body}
      </FieldContextProvider>
    </Form>
  );
}

/**
 * While editing, the title row sticks to the top of the view. Scrolled down
 * the editor so far that the unit's top is out of view above it, this is the
 * viewport y of that line, where the top goes back to; else `null`.
 */
function stickyLineAboveTop(
  card: HTMLElement | null,
  header: HTMLElement | null
): number | null {
  if (!card || !header) return null;
  const scroller = verticalScrollParent(card);
  const viewTop = scroller
    ? scroller.getBoundingClientRect().top + scroller.clientTop
    : 0;
  const line = viewTop + (parseFloat(getComputedStyle(header).top) || 0);
  return card.getBoundingClientRect().top < line - 1 ? line : null;
}

function useTimedFlag(duration: number) {
  const [value, setValue] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );
  return {
    value,
    trigger: () => {
      setValue(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setValue(false), duration);
    }
  };
}
